import { useMemo } from 'react';
import type { AppointmentWithPatient } from '../lib/api';

interface Props {
  anchorDate: Date;
  appointments: AppointmentWithPatient[];
  /** Click su numero del giorno o area vuota → apri overflow modal */
  onDayClick: (iso: string) => void;
  /** Click su singolo appointment dot/row → apri editor view/edit */
  onAppointmentClick: (a: AppointmentWithPatient) => void;
  /** Click sulla "+" della cella → crea con start=quel giorno 09:00 */
  onCreateOnDay: (iso: string) => void;
}

const WEEKDAY_LABELS = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
const WEEKDAY_LABELS_LONG = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/**
 * Calcola la matrice 7×N (5-6 settimane) di celle del mese ancorato.
 * Ogni cella include data ISO e flag `inCurrentMonth`.
 */
function buildMonthMatrix(anchorDate: Date): Array<{ iso: string; date: Date; inCurrentMonth: boolean }> {
  const y = anchorDate.getFullYear();
  const m = anchorDate.getMonth();
  const firstOfMonth = new Date(Date.UTC(y, m, 1));
  let dow = firstOfMonth.getUTCDay();
  if (dow === 0) dow = 7;
  const offsetStart = dow - 1;
  const start = new Date(firstOfMonth);
  start.setUTCDate(start.getUTCDate() - offsetStart);

  const lastOfMonth = new Date(Date.UTC(y, m + 1, 0));
  let dowEnd = lastOfMonth.getUTCDay();
  if (dowEnd === 0) dowEnd = 7;
  const offsetEnd = 7 - dowEnd;
  const end = new Date(lastOfMonth);
  end.setUTCDate(end.getUTCDate() + offsetEnd);

  const cells: Array<{ iso: string; date: Date; inCurrentMonth: boolean }> = [];
  let cur = new Date(start);
  while (cur <= end) {
    const iso = cur.toISOString().slice(0, 10);
    const inCurrentMonth = cur.getUTCMonth() === m;
    cells.push({ iso, date: new Date(cur), inCurrentMonth });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return cells;
}

/**
 * Indicizza appointments per data YYYY-MM-DD locale (basato su start_at UTC).
 * Per UX italiana, la data visualizzata e' la data UTC del start (DB usa UTC).
 */
function indexByDay(appointments: AppointmentWithPatient[]): Map<string, AppointmentWithPatient[]> {
  const map = new Map<string, AppointmentWithPatient[]>();
  for (const a of appointments) {
    // Usa il giorno calendario locale (it-IT) per evitare drift -1 giorno per timezone +0/+2
    const local = new Date(a.start_at);
    const y = local.getFullYear();
    const m = String(local.getMonth() + 1).padStart(2, '0');
    const d = String(local.getDate()).padStart(2, '0');
    const key = `${y}-${m}-${d}`;
    const list = map.get(key) || [];
    list.push(a);
    map.set(key, list);
  }
  // Ordina per orario all'interno di ogni giorno
  for (const [k, list] of map) {
    list.sort((a, b) => a.start_at.localeCompare(b.start_at));
    map.set(k, list);
  }
  return map;
}

function todayLocalIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dotColor(status: string, proRole: 'nutri' | 'coach'): string {
  if (status === 'completed') return 'var(--mute-2)';
  if (status === 'canceled') return 'var(--red)';
  if (status === 'no-show') return 'var(--yellow)';
  // scheduled
  return proRole === 'coach' ? 'var(--orange)' : 'var(--green)';
}

function formatHm(iso: string): string {
  return new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

const TYPE_SHORT: Record<string, string> = {
  visita: 'Visita',
  'follow-up': 'F-up',
  sessione: 'Sess.',
  misurazione: 'Mis.',
  consulenza: 'Cons.',
  altro: 'Altro',
};

export function CalendarMonthView({
  anchorDate,
  appointments,
  onDayClick,
  onAppointmentClick,
  onCreateOnDay,
}: Props) {
  const cells = useMemo(() => buildMonthMatrix(anchorDate), [anchorDate]);
  const byDay = useMemo(() => indexByDay(appointments), [appointments]);
  const todayIso = todayLocalIso();

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
      role="grid"
      aria-label="Calendario mensile"
    >
      {/* Header weekday labels */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--line)',
        }}
        role="row"
      >
        {WEEKDAY_LABELS_LONG.map((label, i) => (
          <div
            key={i}
            role="columnheader"
            style={{
              padding: '10px 12px',
              fontFamily: 'var(--mono)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'var(--mute)',
              textAlign: 'center',
              borderRight: i < 6 ? '1px solid var(--line)' : 'none',
            }}
            aria-label={label}
          >
            <span className="cal-wd-long">{label}</span>
            <span className="cal-wd-short" style={{ display: 'none' }}>
              {WEEKDAY_LABELS[i]}
            </span>
          </div>
        ))}
      </div>

      {/* Cells grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
        }}
        role="rowgroup"
      >
        {cells.map((cell, idx) => {
          const appts = byDay.get(cell.iso) || [];
          const isToday = cell.iso === todayIso;
          const col = idx % 7;
          const row = Math.floor(idx / 7);
          const isLastRow = row === Math.floor((cells.length - 1) / 7);
          const maxVisible = 3;
          const visible = appts.slice(0, maxVisible);
          const overflow = appts.length - visible.length;

          const dayLabel = cell.date.getUTCDate();

          return (
            <div
              key={cell.iso}
              role="gridcell"
              style={{
                background: cell.inCurrentMonth
                  ? 'var(--surface)'
                  : 'var(--surface-2)',
                borderRight: col < 6 ? '1px solid var(--line)' : 'none',
                borderBottom: !isLastRow ? '1px solid var(--line)' : 'none',
                minHeight: 96,
                padding: '8px 8px 6px',
                position: 'relative',
                cursor: appts.length > 0 ? 'pointer' : 'default',
                opacity: cell.inCurrentMonth ? 1 : 0.6,
              }}
              onClick={() => {
                if (appts.length > 0) onDayClick(cell.iso);
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 12,
                    fontWeight: isToday ? 800 : 600,
                    color: isToday ? '#fff' : 'var(--ink-3)',
                    background: isToday ? 'var(--ink)' : 'transparent',
                    width: 22,
                    height: 22,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    lineHeight: 1,
                  }}
                >
                  {dayLabel}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateOnDay(cell.iso);
                  }}
                  aria-label={`Nuovo appuntamento ${cell.iso}`}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'var(--mute-2)',
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                    padding: 0,
                    width: 18,
                    height: 18,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0.4,
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.opacity = '0.4';
                    (e.currentTarget as HTMLButtonElement).style.color = 'var(--mute-2)';
                  }}
                >
                  +
                </button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {visible.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAppointmentClick(a);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                      background: 'transparent',
                      border: 'none',
                      padding: '2px 4px',
                      borderRadius: 5,
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      minWidth: 0,
                      opacity: a.status === 'canceled' ? 0.55 : 1,
                      textDecoration: a.status === 'canceled' ? 'line-through' : 'none',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'var(--surface-2)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                    aria-label={`${a.patient_name} alle ${formatHm(a.start_at)}`}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: dotColor(a.status, a.pro_role),
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontFamily: 'var(--mono)',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: 'var(--ink-3)',
                        flexShrink: 0,
                      }}
                    >
                      {formatHm(a.start_at)}
                    </span>
                    <span
                      style={{
                        fontSize: 11.5,
                        color: 'var(--ink-2)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {a.title || a.patient_name.split(' ')[0]}{' '}
                      <span style={{ color: 'var(--mute)' }}>· {TYPE_SHORT[a.type] || a.type}</span>
                    </span>
                  </button>
                ))}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDayClick(cell.iso);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      padding: '2px 4px',
                      fontSize: 11,
                      color: 'var(--mute)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontWeight: 600,
                    }}
                  >
                    +{overflow} altri
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
